// Puerto verbatim de packages/hce/src/hce/paciente/vitalesResumen.ts.
import type { Observation } from '@medplum/fhirtypes';
import { PA_DIASTOLICA_LOINC, PA_PANEL_LOINC, PA_SISTOLICA_LOINC, SIGNOS_VITALES, type DefinicionSignoVital } from '../lib/vitalSigns';

export interface SerieSignoVital {
  definicion: DefinicionSignoVital;
  observaciones: Observation[];
}

function codigoDe(o: Observation): string | undefined {
  return o.code?.coding?.[0]?.code;
}

export function valorObservacion(o: Observation | undefined): number | undefined {
  return o?.valueQuantity?.value;
}

export function agruparPorSignoVital(observaciones: Observation[]): SerieSignoVital[] {
  return SIGNOS_VITALES.map((definicion) => ({
    definicion,
    observaciones: observaciones.filter((o) => codigoDe(o) === definicion.loinc),
  }));
}

export function elegirSignoVitalConMasHistorial(observaciones: Observation[]): SerieSignoVital | undefined {
  const conDatos = agruparPorSignoVital(observaciones).filter((s) => s.observaciones.length >= 2);
  if (conDatos.length === 0) return undefined;
  return conDatos.reduce((mejor, actual) => (actual.observaciones.length > mejor.observaciones.length ? actual : mejor));
}

export interface PresionArterial {
  sistolica?: number;
  diastolica?: number;
}

export function ultimaPresionArterial(observaciones: Observation[]): PresionArterial | undefined {
  const panel = observaciones.find((o) => codigoDe(o) === PA_PANEL_LOINC);
  if (!panel?.component) return undefined;
  const sistolica = panel.component.find((c) => c.code?.coding?.[0]?.code === PA_SISTOLICA_LOINC)?.valueQuantity?.value;
  const diastolica = panel.component.find((c) => c.code?.coding?.[0]?.code === PA_DIASTOLICA_LOINC)?.valueQuantity?.value;
  if (sistolica === undefined && diastolica === undefined) return undefined;
  return { sistolica, diastolica };
}

export function imcCalculado(observaciones: Observation[]): number | undefined {
  const series = agruparPorSignoVital(observaciones);
  const pesoKg = valorObservacion(series.find((s) => s.definicion.clave === 'peso')?.observaciones[0]);
  const tallaCm = valorObservacion(series.find((s) => s.definicion.clave === 'talla')?.observaciones[0]);
  if (pesoKg === undefined || tallaCm === undefined || tallaCm <= 0) return undefined;
  const tallaM = tallaCm / 100;
  return pesoKg / (tallaM * tallaM);
}
